import { useCallback, useState } from 'react';
import { Alert, App, Button, Radio, Space } from 'antd';
import { useAppStore } from '@/store';
import { ReserveHttp } from '@/services';
import { AppModal, Flex } from '@/components';
import { useTranslation } from 'react-i18next';

type ModalData = { reserve: UserWeekReserveEntity, meal: SelfMealEntity; onChangeWeek: () => void };

export default function ChangeSelfModal({
  open,
  hide,
  variant,
  onSuccess = undefined,
  data = { reserve: null, meal: null, onChangeWeek: () => { } } as ModalData
}) {

  const { fetchCredit } = useAppStore();
  const { message } = App.useApp();
  const [t] = useTranslation();
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onCancel = useCallback(() => {
    hide();
    setSelected(null);
  }, [setSelected, hide]);

  const handleSubmit = useCallback(async () => {
    const { reserve } = (data ?? {});
    setSubmitting(true);

    try {
      await ReserveHttp.changeSelf({
        date: reserve.programDate,
        destinationSelfId: selected,
        foodTypeId: reserve.foodTypeId,
        sourceSelfId: reserve.selfId,
        foodId: reserve.foodId,
        groupId: reserve.groupId,
        mealTypeId: reserve.mealTypeId,
        programId: reserve.programId,
        reserveId: reserve.id
      });
      message.success(t('reserve:messages.success-transfer'));
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      onCancel();
      fetchCredit();
    }
    catch (e) { }
    finally {
      setSubmitting(false);
    }
  }, [selected, onCancel, onSuccess, data, fetchCredit, message, t]);

  let toll = (data?.reserve?.selfChangeToll || 0);
  if (toll > 0) {
    toll = toll / 10;
  }

  const selfType = t(`reserve:labels.choose-self-prefix.${variant}`);

  return (
    <AppModal
      centered
      destroyOnHidden
      open={open}
      footer={null}
      variant={variant}
      onCancel={onCancel}
      style={{ maxWidth: '90%' }}
      title={t(`reserve:labels.change-self.${variant}`)}
    >
      <Flex inlineFlex className="pb-2 text-mid">
        {t('reserve:messages.choose-place-for-change', { value: selfType })}
      </Flex>
      <Flex className="pt-0" direction="column">
        <Radio.Group value={selected} onChange={(e) => setSelected(e.target.value)}>
          <Space direction="vertical">
            {data?.reserve?.changableSelfs?.map(([id, name]) => (
              <Radio key={id} value={id} name="radio-change-self">{name}</Radio>
            ))}
          </Space>
        </Radio.Group>
        <Alert
          type="warning"
          className="my-2"
          message={(
            <Flex className="p-0 m-0 text-sm list-none" component="ul" direction="column">
              <span>
                {t('reserve:messages.confirm-change-self-fee', { fee: toll })}
              </span>
            </Flex>
          )} />
        <Flex inlineFlex>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={selected == null}
            className="ms-auto mt-2">
            {t(`reserve:context.change-self.${variant}`)}
          </Button>
        </Flex>
      </Flex>
    </AppModal>
  );
}
